import { getShortName } from '@/utils/userNames'
import { GetUserType } from '@orbit/api-types'

/**
 * Trip members ordered for display: the current user first, everyone else in
 * the order they arrived. Every member picker in the app puts "you" at the top,
 * so the rule lives here rather than in each surface.
 */
export const sortMembersWithCurrentFirst = (
	members: GetUserType[],
	currentUserId: number | undefined,
): GetUserType[] =>
	[...members].sort((a, b) => {
		if (a.id === currentUserId) return -1
		if (b.id === currentUserId) return 1
		return 0
	})

/** "(You)" suffix on the member's own row, so a picker names who is who. */
const withYouSuffix = (name: string, isCurrentUser: boolean): string =>
	isCurrentUser ? `${name} (You)` : name

/** Full name for a roomy picker row — "Alice Anderson", "Alice Anderson (You)". */
export const fullMemberName = (
	member: GetUserType,
	currentUserId: number | undefined,
): string =>
	withYouSuffix(
		[member.firstName, member.lastName].filter(Boolean).join(' '),
		member.id === currentUserId,
	)

/** "First L." for a tight picker row — "Alice A.", "Alice A. (You)". */
export const shortMemberName = (
	member: GetUserType,
	currentUserId: number | undefined,
): string => withYouSuffix(getShortName(member), member.id === currentUserId)

/** A person's full name as a member list shows it, or "No name yet" for an account that hasn't given one. */
export const memberListName = (member: GetUserType): string => fullMemberName(member, undefined) || 'No name yet'

/**
 * Whether I own the trip — the one who answers Join requests, removes members
 * and flips Join approval. Undefined until both my profile and the trip are in.
 */
export const isTripOwner = (
	me: { id: number } | undefined,
	trip: { ownerUserId: number } | undefined,
): boolean | undefined => (me && trip ? me.id === trip.ownerUserId : undefined)
